import { useState } from "react";
import { useDeleteCompanyData, useGetCompanyList } from "../../../../api/company";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { CompanyListViewModel, CompanyRow } from "../types/companyListView.types";

const DEFAULT_PAGE_SIZE = 10;

/**
 * Owns every piece of state/data-fetching the legacy `CompanyList` kept
 * inline (src/screens/company/companyList/CompanyList.tsx) — same server-side
 * pagination with a selectable page size, same delete flow (soft-delete
 * mutation, "Company inactive successfully" toast, list refetch), same
 * Register / Details / Edit / Recipe-Ingredient / Generic-Process modal
 * wiring. Ported logic-for-logic; only the presentation changed.
 */
export const useCompanyListViewModel = (): CompanyListViewModel => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [visibleRegisterModal, setVisibleRegisterModal] = useState(false);
  const [detailsId, setDetailsId] = useState("");
  const [editId, setEditId] = useState("");
  const [visibleDetailsModal, setVisibleDetailsModal] = useState(false);
  const [visibleEditModal, setVisibleEditModal] = useState(false);
  const [visibleDeleteConfirm, setVisibleDeleteConfirm] = useState(false);
  const [deleteData, setDeleteData] = useState<CompanyRow | null>(null);
  const [recipeCompany, setRecipeCompany] = useState<CompanyRow | null>(null);
  const [genericProcessCompany, setGenericProcessCompany] = useState<CompanyRow | null>(null);

  const {
    data: companyData,
    isLoading: isRowsLoading,
    refetch
  } = useGetCompanyList(pageNumber, pageSize);
  const { isLoading: isDeleting, mutate: deleteCompany } = useDeleteCompanyData();

  const handlePageSize = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const closeRegisterModal = () => setVisibleRegisterModal(false);
  const onSuccessRegister = () => {
    setVisibleRegisterModal(false);
    refetch();
  };

  const openDetailsModal = (row: CompanyRow) => {
    setDetailsId(row._id);
    setVisibleDetailsModal(true);
  };
  const closeDetailsModal = () => setVisibleDetailsModal(false);
  const navigateDetailsToEdit = () => {
    setEditId(detailsId);
    setVisibleDetailsModal(false);
    setVisibleEditModal(true);
  };

  const openEditModal = (row: CompanyRow) => {
    setEditId(row._id);
    setVisibleEditModal(true);
  };
  const closeEditModal = () => setVisibleEditModal(false);
  const onSuccessEdit = () => {
    setVisibleEditModal(false);
    refetch();
  };

  const openDeleteConfirm = (row: CompanyRow) => {
    setDeleteData(row);
    setVisibleDeleteConfirm(true);
  };
  const closeDeleteConfirm = () => {
    setVisibleDeleteConfirm(false);
    setDeleteData(null);
  };
  const confirmDelete = () => {
    const target = deleteData;
    setVisibleDeleteConfirm(false);
    setDeleteData(null);
    if (!target?._id) return;
    deleteCompany(target._id, {
      onSuccess: () => {
        successMessage("Company inactive successfully");
        refetch();
      }
    });
  };

  return {
    rows: companyData?.result || [],
    isRowsLoading,
    totalCount: companyData?.total || 0,
    totalPages: Math.ceil((companyData?.total || 0) / pageSize) || 1,
    pageNumber,
    pageSize,
    handlePagination: setPageNumber,
    handlePageSize,

    visibleRegisterModal,
    openRegisterModal: () => setVisibleRegisterModal(true),
    closeRegisterModal,
    onSuccessRegister,

    detailsId,
    editId,
    visibleDetailsModal,
    visibleEditModal,
    openDetailsModal,
    closeDetailsModal,
    navigateDetailsToEdit,
    openEditModal,
    closeEditModal,
    onSuccessEdit,

    visibleDeleteConfirm,
    openDeleteConfirm,
    closeDeleteConfirm,
    confirmDelete,
    isDeleting,

    recipeCompany,
    openRecipeModal: setRecipeCompany,
    closeRecipeModal: () => setRecipeCompany(null),
    genericProcessCompany,
    openGenericProcessModal: setGenericProcessCompany,
    closeGenericProcessModal: () => setGenericProcessCompany(null)
  };
};
